import { useEffect, useState } from 'react';
import { joinWindowState, type JoinWindowInput } from '@hiredude/meet-shared';

import { formatDateTime, formatRelativeTo } from '@/components/admin/format';
import { JoinLinkStatusBadge } from '@/components/admin/status';
import { cn } from '@/lib/utils';

const TICK_MS = 30_000;

export function useNow(intervalMs = TICK_MS): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  return now;
}

/**
 * Why this meeting's join links will or will not work, in one sentence.
 *
 * The URL itself never goes stale as a string. The server refuses it when the
 * interview is cancelled or the host has ended it.
 */
export function joinWindowCopy(input: JoinWindowInput, now = new Date()): string {
  const state = joinWindowState(input, now);

  switch (state) {
    case 'open':
      return 'The link works any time until the interview is cancelled or the host ends it.';
    case 'too_early':
      return `Links start working at ${formatDateTime(input.joinOpensAt)} (${formatRelativeTo(input.joinOpensAt, now)}).`;
    case 'too_late':
      return `The join window closed at ${formatDateTime(input.joinClosesAt)} (${formatRelativeTo(input.joinClosesAt, now)}).`;
    case 'ended':
      return 'The interview has finished, so these links will not admit anyone.';
    case 'cancelled':
      return 'This interview was cancelled, so these links will not admit anyone.';
  }
}

export function JoinWindowSummary({
  input,
  className,
  compact = false,
  showBadge = true,
}: {
  input: JoinWindowInput;
  className?: string;
  compact?: boolean;
  showBadge?: boolean;
}) {
  const now = useNow();
  const state = joinWindowState(input, now);

  return (
    <div className={cn('min-w-0', className)}>
      {showBadge && <JoinLinkStatusBadge state={state} />}
      <p
        className={cn(
          'text-muted-foreground text-pretty',
          compact ? 'text-[11px] leading-4' : 'text-xs',
          showBadge && 'mt-1.5',
        )}
      >
        {joinWindowCopy(input, now)}
      </p>
    </div>
  );
}
